import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
import type { LabelProps } from '@Shelly/shelly-design-system/index';
import type { CloudRecordingClassificationObject } from '../types/cloud-recordings';
import type {
	BarColor,
	CameraObjectDef,
	DetectedObjectBox,
	DetectedObjectRow,
} from '../types/camera-objects';
import { getClassificationObjects } from 'src/services/cam-cloud/recordings';

export const DEFAULT_OBJECT_BAR_COLOR: BarColor = 'white';
export const DEFAULT_OBJECT_ICON = 'fas fa-tag';

const UNKNOWN_OBJECT_TRANSLATION_KEY = 'camera_object_unknown';
const UNKNOWN_OBJECT_DEFAULT_LABEL = 'object';

/*
 * Camera object definitions: icon, color and translation.

 * The model's vocabulary is backend-owned: an unmapped class
 * falls back to a generic icon and color.
 */
export const CAMERA_OBJECT_DEFS: Record<string, CameraObjectDef> = {
	person: {
		i18n: 'camera_object_person',
		icon: 'fas fa-person',
		iconColor: '--color-primary',
		barColor: 'primary',
	},
	dog: {
		i18n: 'camera_object_dog',
		icon: 'fas fa-dog',
		iconColor: '--color-electric-vehicle',
		barColor: 'electric-vehicle',
	},
	cat: {
		i18n: 'camera_object_cat',
		icon: 'fas fa-cat',
		iconColor: '--color-entertainment',
		barColor: 'entertainment',
	},
	bird: { i18n: 'camera_object_bird', icon: 'fas fa-dove' },
	rabbit: { i18n: 'camera_object_rabbit', icon: 'fas fa-rabbit' },
	car: {
		i18n: 'camera_object_car',
		icon: 'fas fa-car',
		iconColor: '--color-cooking',
		barColor: 'cooking',
	},
	van: {
		i18n: 'camera_object_van',
		icon: 'fas fa-van-shuttle',
		iconColor: '--color-heating',
		barColor: 'heating',
	},
	truck: {
		i18n: 'camera_object_truck',
		icon: 'fas fa-truck',
		iconColor: '--color-heating',
		barColor: 'heating',
	},
	bus: { i18n: 'camera_object_bus', icon: 'fas fa-bus' },
	motorcycle: { i18n: 'camera_object_motorcycle', icon: 'fas fa-motorcycle' },
	bicycle: { i18n: 'camera_object_bicycle', icon: 'fas fa-bicycle' },
	handgun: { i18n: 'camera_object_handgun', icon: 'fas fa-gun' },
	rifle: { i18n: 'camera_object_rifle', icon: 'fas fa-person-rifle' },
	knife: { i18n: 'camera_object_knife', icon: 'fas fa-knife' },
};

/** Class names we ship a definition for, ordered as in `CAMERA_OBJECT_DEFS`. */
export const KNOWN_CAMERA_OBJECT_NAMES = Object.keys(CAMERA_OBJECT_DEFS);

/*
 * Class names are compared and rendered lowercased, so a class reported with
 * different casing or padding still resolves to the same definition.
 *
 * @param name - A class name as reported by the model or read from a scene.
 * @returns The normalised name, or an empty string when there is nothing to read.
 */
export const normalizeCameraObjectName = (name?: unknown): string =>
	typeof name === 'string' ? name.trim().toLowerCase() : '';

/*
 * @param name - A class name as reported by the model or read from a scene.
 * @returns The shipped definition, or undefined for a class we do not know.
 */
export const getCameraObjectDef = (name?: unknown): CameraObjectDef | undefined =>
	CAMERA_OBJECT_DEFS[normalizeCameraObjectName(name)];

export const getCameraObjectIcon = (name?: unknown): string =>
	getCameraObjectDef(name)?.icon ?? DEFAULT_OBJECT_ICON;

export const getCameraObjectBarColor = (name?: unknown): BarColor =>
	getCameraObjectDef(name)?.barColor ?? DEFAULT_OBJECT_BAR_COLOR;

/*
 * Human-readable name for a reported class.
 *
 * A class we ship no definition for still renders, falling back to its raw name
 * rather than to nothing.
 *
 * @param name - A class name as reported by the model or read from a scene.
 * @returns The translated class name, or a generic label when there is no name.
 */
export const getCameraObjectLabel = (name?: unknown): string => {
	const _format = get(format);
	const normalized = normalizeCameraObjectName(name);

	if (!normalized) {
		return _format(UNKNOWN_OBJECT_TRANSLATION_KEY, { default: UNKNOWN_OBJECT_DEFAULT_LABEL });
	}

	const fallback = normalized.replace(/[_-]+/g, ' ');
	const i18n = getCameraObjectDef(normalized)?.i18n;

	return i18n ? _format(i18n, { default: fallback }) : fallback;
};

/*
 * Label chip for a reported class.
 *
 * @param name - A class name as reported by the model or read from a scene.
 * @param value - The composed chip text, which may carry a count prefix (`2x dog`).
 * @returns Label props carrying the class icon, and its colour when we have one.
 */
export const getCameraObjectLabelProps = (name: unknown, value: string): LabelProps => {
	const def = getCameraObjectDef(name);

	if (!def) {
		return { value, icon: DEFAULT_OBJECT_ICON };
	}

	const labelProps: LabelProps = { value, icon: def.icon };

	if (!def.iconColor) {
		return labelProps;
	}

	return { ...labelProps, iconColor: def.iconColor };
};

/**
 * Label chips for the classes detected in one recording, counting repeats so
 * three people read as a single `3x person` chip.
 *
 * @param objects - The classification objects reported for a recording.
 * @returns One label per distinct class.
 */
export const getObjectLabels = (objects: CloudRecordingClassificationObject[]): LabelProps[] => {
	const counts = new Map<string, number>();
	for (const obj of objects) {
		const name = obj?.name?.toLowerCase();
		if (!name) continue;
		counts.set(name, (counts.get(name) ?? 0) + 1);
	}

	return Array.from(counts, ([name, count]) => {
		const value = getCameraObjectLabel(name);
		return getCameraObjectLabelProps(name, count > 1 ? `${count}x ${value}` : value);
	});
};

/** Shared id between list rows and bounding boxes so they can be cross-referenced. */
export const getDetectedObjectId = (rawIndex: number): string => `obj-${rawIndex}`;

export const getDetailedObjectRows = (
	objects: CloudRecordingClassificationObject[],
): DetectedObjectRow[] =>
	(objects ?? [])
		.map((o, idx) => {
			if (!o?.name) return null;
			const percent = Math.round((o.score ?? 0) * 100);
			const row: DetectedObjectRow = {
				id: getDetectedObjectId(idx),
				icon: getCameraObjectIcon(o.name),
				name: getCameraObjectLabel(o.name),
				percent,
				barColor: getCameraObjectBarColor(o.name),
			};
			return row;
		})
		.filter((r): r is DetectedObjectRow => r !== null);

const toDetectedObjectBox = (
	detectedObject: CloudRecordingClassificationObject,
	rawIndex: number,
	naturalWidth: number,
	naturalHeight: number,
): DetectedObjectBox | null => {
	const box = detectedObject?.boundingBox;
	if (!box || box.length !== 4 || !naturalWidth || !naturalHeight) return null;
	const [x1, y1, x2, y2] = box;
	const left = Math.min(x1, x2);
	const top = Math.min(y1, y2);
	const right = Math.max(x1, x2);
	const bottom = Math.max(y1, y2);
	const width = right - left;
	const height = bottom - top;
	if (width <= 0 || height <= 0) return null;
	const percent = Math.round((detectedObject.score ?? 0) * 100);
	return {
		id: getDetectedObjectId(rawIndex),
		leftPct: (left / naturalWidth) * 100,
		topPct: (top / naturalHeight) * 100,
		widthPct: (width / naturalWidth) * 100,
		heightPct: (height / naturalHeight) * 100,
		label: `${detectedObject.name} ${percent}%`,
		color: `var(--color-${getCameraObjectBarColor(detectedObject.name)})`,
	};
};

export const getDetectedObjectBoxes = (
	objects: CloudRecordingClassificationObject[],
	naturalWidth: number,
	naturalHeight: number,
): DetectedObjectBox[] => {
	if (!naturalWidth || !naturalHeight) return [];
	return (objects ?? [])
		.map((obj, idx) => toDetectedObjectBox(obj, idx, naturalWidth, naturalHeight))
		.filter((b): b is DetectedObjectBox => b !== null);
};

let cachedObjectNames: string[] | null = null;

/*
 * Puts the classes we ship a definition for first, as those are the common ones
 * users look for, and keeps the rest in the order the backend returned them.
 *
 * @param names - The class names as returned by the backend.
 * @returns The normalised, deduplicated names in display order.
 */
const orderObjectNames = (names: string[]): string[] => {
	const unique = Array.from(new Set(names.map(normalizeCameraObjectName).filter(Boolean)));

	return [
		...KNOWN_CAMERA_OBJECT_NAMES.filter((name) => unique.includes(name)),
		...unique.filter((name) => !KNOWN_CAMERA_OBJECT_NAMES.includes(name)),
	];
};

/*
 * The object classes the recognition model can report, fetched on first use and
 * cached for the rest of the session.
 *
 * Only a usable response is cached, so a failed or empty request falls back to the
 * classes we ship definitions for and is retried the next time it is needed.
 *
 * @returns The class names in display order.
 */
export const loadCameraObjectNames = async (): Promise<string[]> => {
	if (cachedObjectNames) {
		return cachedObjectNames;
	}

	const names = await getClassificationObjects().catch((err) => {
		console.error('[Camera] Error fetching classification object types', err);
		return [];
	});

	const ordered = orderObjectNames(names);

	if (!ordered.length) {
		return KNOWN_CAMERA_OBJECT_NAMES;
	}

	cachedObjectNames = ordered;
	return cachedObjectNames;
};
